import { toast } from 'sonner'
import { InfoFilledIcon } from '@/components/ui/custom-icons'

/**
 * Shown after an external paste whose source font is not in this account's permissible families
 * (WMPCM-9199).
 *
 * The fallback itself is correct and deliberate — a family the account is not licensed for cannot
 * be placed on a printed page, so the run keeps the caret's font. What was wrong was that it
 * happened silently, which reads to the user as "paste lost my formatting" and is indistinguishable
 * from the case where a family IS available and the lookup simply missed it (the case
 * `normalizeFontFamilyKey` and `resolveFontFamilyFromCssStack` now handle).
 *
 * Named families, deduped and capped, because "some fonts changed" is not actionable — knowing it
 * was Calibri tells the user what to pick instead.
 */
const MAX_NAMED_FAMILIES = 3

export function notifyPastedFontsUnavailable(cssFontFamilies: ReadonlySet<string>): void {
  if (cssFontFamilies.size === 0) return

  // Each entry is a raw CSS `font-family` value, so it may be a whole fallback stack. The head is
  // the family the source actually asked for; the rest is the browser's fallback chain and is noise
  // to the user.
  const names = [
    ...new Set([...cssFontFamilies].map((v) => v.split(',')[0]!.trim()).filter(Boolean)),
  ]

  if (names.length === 0) return

  const shown = names.slice(0, MAX_NAMED_FAMILIES).join(', ')
  const rest = names.length - MAX_NAMED_FAMILIES
  const list = rest > 0 ? `${shown} and ${rest} more` : shown

  toast.info(`${list} ${names.length === 1 ? 'is' : 'are'} not available in your book.`, {
    description: 'Pasted text kept its other formatting and uses the surrounding font.',
    icon: <InfoFilledIcon size={20} className="text-black" />,
  })
}
